import type { CustomChart } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { convertApiToZoom } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom-constants"
import { useAtomValue } from "jotai"
import { Suspense, useMemo } from "react"
import { getTooltipOptionsFromChartSettingsAtomFamily } from "../../store/chart/tooltip-options-atom"
import { getYAxisOptionsFromChartSettingsAtomFamily } from "../../store/chart/yaxis-options-atom"
import {
  type ChartSerie,
  type ChartSerieTimeData,
  type GranularityType,
  Chart as UIChart,
  type ChartProps as UIChartProps,
} from "../../tt-ui-chart/Chart"
import { BarSkeleton } from "../../tt-ui-chart/ChartLoadingIndicator"
import type { TooltipOptions } from "../../tt-ui-chart/Tooltip"
import {
  getColorPaletteColorVariableById,
  isColorPaletteColorId,
} from "../../utils/colors"

type InnerBaseProps = {
  chartSerieSettings: CustomChart
  series: ChartSerie[]
  seriesData: Record<string, ChartSerieTimeData>
  isVisible?: boolean
}

type BaseChartProps = Pick<
  InnerBaseProps,
  "chartSerieSettings" | "series" | "seriesData" | "isVisible"
> & {
  hideYAxisLabels?: boolean
  disableClickToOpenModal?: boolean
  enableDataGrouping?: boolean
  /** Override / extend the tooltip options derived from the metric config.
   * The metric-config lookup returns `undefined` for synthetic (unregistered)
   * metrics, so callers rendering client-composed series can pass their own
   * options here (e.g. `{ showTotal: true }` to force the tooltip's Total row).
   * Merged over the derived options; omit to keep the derived behavior. */
  tooltipOptionsOverride?: Partial<TooltipOptions>
}

export function BaseChart({
  chartSerieSettings,
  series,
  seriesData,
  isVisible,
  hideYAxisLabels = false,
  disableClickToOpenModal = false,
  enableDataGrouping = false,
  tooltipOptionsOverride,
}: BaseChartProps) {
  const zoom = chartSerieSettings.zoom
  const granularity = chartSerieSettings.granularity as GranularityType
  const showCurrentPeriod = chartSerieSettings.show_current_period ?? false
  const hidePartialPeriod = chartSerieSettings.hide_partial_period ?? false

  const yAxisOptions: UIChartProps["yAxisOptions"] = useAtomValue(
    getYAxisOptionsFromChartSettingsAtomFamily({
      chartSettings: chartSerieSettings.configs,
      hideYAxisLabels,
    })
  )
  const derivedTooltipOptions = useAtomValue(
    getTooltipOptionsFromChartSettingsAtomFamily(chartSerieSettings.configs)
  )
  const tooltipOptions = tooltipOptionsOverride
    ? { ...derivedTooltipOptions, ...tooltipOptionsOverride }
    : derivedTooltipOptions

  const seriesWithParsedColors = useMemo(() => {
    return series.map((serie) => {
      const result = JSON.parse(JSON.stringify(serie)) as ChartSerie
      if (isColorPaletteColorId(serie.color || "")) {
        // Can cast the color as a string since we know it's a color palette color ID
        result.color = getColorPaletteColorVariableById(serie.color as string)
      }

      return result
    })
  }, [series])

  return (
    <Suspense
      fallback={
        <BarSkeleton
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
          }}
        />
      }
    >
      {isVisible ? (
        <UIChart
          series={seriesWithParsedColors}
          seriesData={seriesData}
          zoom={convertApiToZoom(zoom)}
          granularity={granularity}
          yAxisOptions={yAxisOptions}
          showNavigator={false}
          isTimeBased
          tooltipOptions={tooltipOptions}
          legend={false}
          showCurrentPeriod={showCurrentPeriod}
          hidePartialPeriod={hidePartialPeriod}
          reverseBarOrder={chartSerieSettings?.configs.some(
            (config) => config.reverseBarOrder
          )}
          disableClickToOpenModal={disableClickToOpenModal}
          enableDataGrouping={enableDataGrouping}
        />
      ) : null}
    </Suspense>
  )
}
